import { useTranslation } from '@conekta/cronos/i18n';
import { CheckoutFrameContext } from 'app/context';
import { Fingerprint } from 'app/pages/frames/helpers/fingerprint';
import {
  createOrderWithCreditCardService,
  paymentSourceService,
  preAuthService,
} from 'app/pages/frames/http/orderService';
import { use3DSFlow } from 'app/pages/frames/lib/3DS';
import { ThreeDsValues } from 'app/util/constants';
import { getDifferenceInSeconds } from 'app/util/dates';
import get from 'lodash.get';
import { useContext, useEffect, useState } from 'react';

interface Props {
  paymentRequestData: any;
  initialTime: Date;
  orderPaymentSourceData?: any;
  binNumber?: string;
  formCard?: Record<string, any>;
}

export const useCreditCardSubtmitOrder = () => {
  const isAvailableThreeDS: string[] = [ThreeDsValues.enable, ThreeDsValues.dynamic];
  const [error, setError] = useState<Error | null>(null);
  const { entity, checkoutRequest } = useContext(CheckoutFrameContext);
  const [threeDsFlag, setThreeDsFlag] = useState<any>(entity?.threeDs || ThreeDsValues.off);
  const { t } = useTranslation();
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { dispose, execute: execute3DSFlow } = use3DSFlow();

  useEffect(() => {
    validate3DS();
    return () => dispose();
  }, []);

  async function doPostWrapper(props: Props) {
    setIsLoading(true);
    await doPost(props);
    setIsLoading(false);
  }

  function validate3DS() {
    const { threeDs } = entity || { threeDs: '' };
    if (threeDs === '') {
      setError({
        name: 'error_3ds',
        message: t(`iFrame.checkout.cardForm.errorMisc`),
      });
    } else {
      setThreeDsFlag(threeDs as ThreeDsValues);
    }
  }

  async function createOrder(dataRequest: { binNumber?: string; fingerprint?: string; data: Record<string, any> }) {
    try {
      const orderResponse = await createOrderWithCreditCardService(dataRequest);
      if (orderResponse.paymentStatus === 'error') {
        const errorMessage = get(orderResponse.errors, '[0].message', '');
        setError(Error(errorMessage));
        return;
      }
      return orderResponse;
    } catch (e) {
      throw e;
    }
  }

  const startProcess = async ({ formCard, preAuth, binNumber, orderBody, fingerprint }: Record<string, any>) => {
    const {
      recurrent = false,
      orderTemplate: { customerInfo: customerInfoCheckout = {} },
    } = checkoutRequest || {};
    const customerInfo = recurrent ? formCard.customerInfo : customerInfoCheckout;

    if (preAuth.threeDsFlowRequired) {
      const additionalOrderData = await execute3DSFlow(checkoutRequest, preAuth, formCard, customerInfo, entity?.name);
      return createOrder({ binNumber, fingerprint, data: { ...orderBody, ...additionalOrderData } });
    }
    return createOrder({ fingerprint: orderBody.fingerprint, binNumber, data: orderBody });
  };

  async function doPost({ paymentRequestData, initialTime, orderPaymentSourceData, binNumber, formCard = {} }: Props) {
    try {
      const fingerprint = await Fingerprint.getVisitorId();
      let orderPaymentSourceRes: any, preAuth: any, createOrderResponse: any;

      paymentRequestData.fingerprint = fingerprint;
      if (orderPaymentSourceData) {
        orderPaymentSourceData.fingerprint = fingerprint;
        orderPaymentSourceRes = orderPaymentSourceData;
      } else {
        orderPaymentSourceRes = await paymentSourceService(paymentRequestData);
      }

      const timeTookToFillPaymentForm = getDifferenceInSeconds(new Date(), initialTime);

      const { customerInfo, paymentKey = '', paymentSourceId = '' } = orderPaymentSourceRes || {};
      if (!customerInfo) {
        setError(Error('customerId was not returned from "orderPayment"'));
        return;
      }

      const orderBody = {
        ...paymentRequestData,
        customerInfo: {
          ...customerInfo,
        },
        fillPaymentFormTime: timeTookToFillPaymentForm,
        fingerprint: fingerprint,
        paymentKey,
        paymentSourceId,
      };

      if (isAvailableThreeDS.includes(threeDsFlag)) {
        const preAuthPostData: any = await preAuthService({
          ...paymentRequestData,
          paymentSourceId,
        });
        preAuth = preAuthPostData;
        orderBody.checkoutAntifraudResponseID = preAuthPostData.checkoutAntifraudResponseId;
        createOrderResponse = await startProcess({ preAuth, binNumber, orderBody, formCard, fingerprint });
      } else {
        createOrderResponse = await createOrder({ binNumber, fingerprint, data: orderBody });
      }
      setData((prev: any) => ({
        ...prev,
        order: createOrderResponse,
        preAuth: preAuth,
        paymentSource: orderPaymentSourceRes,
      }));
    } catch (e: any) {
      setError(e);
    }
  }

  return {
    data,
    threeDsFlag,
    error,
    isLoading,
    doPost: doPostWrapper,
  };
};
